import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useCreateOrg } from '../api/orgs';
import { useMe } from '../api/session';

/**
 * Create-only flow at `/orgs/new`. Editing an existing org now lives on the
 * unified detail page at `/orgs/:id`. The route `/orgs/:id/edit` redirects
 * there for old bookmarks.
 */
export function OrgEditPage() {
  const navigate = useNavigate();
  const createOrg = useCreateOrg();
  const meQuery = useMe();
  const isSuperAdmin = meQuery.data?.role === 'super_admin';
  const [synthetic, setSynthetic] = useState(false);

  const [name, setName] = useState('');
  const [region, setRegion] = useState<'uk' | 'us'>('uk');
  const [error, setError] = useState<string | null>(null);

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    if (trimmed.length > 255) {
      setError('Name must be 255 characters or fewer.');
      return;
    }

    createOrg.mutate(
      { name: trimmed, region, ...(isSuperAdmin && synthetic ? { synthetic: true } : {}) },
      {
        onSuccess: (org) => navigate(`/orgs/${org.id}`),
        onError: (err) => setError(formatApiError(err, 'Failed to create.')),
      },
    );
  };

  return (
    <Stack gap="lg">
      <Title order={2}>New organisation</Title>

      <Card shadow="sm" padding="lg" maw={600}>
        <Box component="form" onSubmit={handleSave} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}

            <TextInput
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={createOrg.isPending}
            />

            <NativeSelect
              label="Region"
              required
              data={[
                { value: 'uk', label: 'UK' },
                { value: 'us', label: 'US' },
              ]}
              value={region}
              onChange={(e) => setRegion(e.currentTarget.value as 'uk' | 'us')}
              disabled={createOrg.isPending}
            />

            {isSuperAdmin && (
              <Stack gap={4}>
                <Checkbox
                  label="Synthetic test org"
                  checked={synthetic}
                  onChange={(e) => setSynthetic(e.currentTarget.checked)}
                  disabled={createOrg.isPending}
                />
                <Text size="xs" c="dimmed" ml={28}>
                  Reserves this org for the simulator. One-way — cannot be undone.
                </Text>
              </Stack>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => navigate('/orgs')}>
                Cancel
              </Button>
              <Button type="submit" loading={createOrg.isPending}>
                Save
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}

function formatApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Validation failed. Check the highlighted fields.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
    if (err.status === 409) return 'An organisation with that name already exists in this region.';
  }
  return fallback;
}
